import AppButton from 'components/AppButton/AppButton';
import {
    DocumentOpenIcon,
    ResetIcon,
    TrashIcon,
    EnvelopeIcon,
} from 'evergreen-ui';
import React, { FunctionComponent, useMemo } from 'react';
import styles from './TargetGroupTableFooter.module.css';
import { getPaginationRange, isSearchedParticipant } from '../../utils';
import { Participant } from '../../../../typings/participant';
import { SearchedParticipant } from '../../../../store/api/search';

interface TargetGroupTableFooterProps {
    isBatchProcessLoading: boolean;
    candidateTotalPages: number;
    candidateCurrentPage: number;
    isSuperAdmin: boolean;
    participantsSelected?: Participant[] | SearchedParticipant[];
    selectedIds?: Set<string>;
    participants?: Participant[] | SearchedParticipant[];
    groupType?: string;
    onSendResetPasswordEvent: () => void;
    onSendReminderEvent: () => void;
    onSendReportEvent: () => void;
    onRemoveCandidateEvent: () => void;
    onFullDeleteCandidateEvent: () => void;
    onResetCandidateScanEvent: () => void;
    setSearchParams: (params: any) => unknown;
    setSelectedParticipants: (params: any) => unknown;
}

const TargetGroupTableFooter: FunctionComponent<TargetGroupTableFooterProps> = (
    props: TargetGroupTableFooterProps,
) => {
    const {
        isBatchProcessLoading,
        candidateTotalPages,
        candidateCurrentPage,
        isSuperAdmin,
        participantsSelected,
        selectedIds,
        participants,
        groupType,
        onSendResetPasswordEvent,
        onSendReminderEvent,
        onSendReportEvent,
        onRemoveCandidateEvent,
        onFullDeleteCandidateEvent,
        onResetCandidateScanEvent,
        setSearchParams,
        setSelectedParticipants,
    } = props;

    const selectedParticipants = useMemo(() => {
        if (participantsSelected) {
            // Use an existing array when coming from Group View
            return participantsSelected;
        }
        if (selectedIds && participants) {
            // Convert Set to array when coming from search
            return participants.filter((p) =>
                selectedIds.has(
                    `${p.user_id}||${(p as any).group_id || 'defaultGroup'}`,
                ),
            );
        }
        return [];
    }, [participantsSelected, selectedIds, participants]);

    const selectedParticipantsThatCanReceiveReport = useMemo(() => {
        return selectedParticipants.filter((participant) => {
            const hasReport = isSearchedParticipant(participant)
                ? !!participant.report_generated_at
                : !!(participant as Participant).report;
            const isAnonMotiview =
                !participant.email && groupType === 'MOTIVIEW';
            return hasReport && !isAnonMotiview;
        });
    }, [selectedParticipants, groupType]);

    const selectedParticipantsThatAreNotDeletable = useMemo(() => {
        return selectedParticipants.filter((participant) =>
            isSearchedParticipant(participant)
                ? !!participant.report_generated_at
                : !!(participant as Participant).analysis,
        );
    }, [selectedParticipants]);

    const selectedParticipantsWithEmail = useMemo(() => {
        return selectedParticipants.filter(
            (participant) => !(!participant.email && groupType === 'MOTIVIEW'),
        );
    }, [selectedParticipants, groupType]);

    const paginationRange = useMemo(() => {
        return getPaginationRange(candidateCurrentPage, candidateTotalPages, 1);
    }, [candidateCurrentPage, candidateTotalPages]);

    const isSendReminderOrResetDisabled =
        selectedParticipants.length === 0 ||
        selectedParticipantsWithEmail.length === 0;
    const isSendReportDisabled =
        selectedParticipants.length === 0 ||
        selectedParticipantsThatCanReceiveReport.length === 0;
    const isDeleteDisabled =
        selectedParticipants.length === 0 ||
        selectedParticipantsThatAreNotDeletable.length ===
            selectedParticipants.length;
    const usersCannotBeDeleted =
        selectedParticipants.length > 0 &&
        selectedParticipantsThatAreNotDeletable.length ===
            selectedParticipants.length;
    return (
        <div className={styles.footer}>
            <div className={styles.pages_wrapper}>
                {candidateTotalPages > 1 &&
                    paginationRange.map((page, index) => {
                        if (typeof page === 'string') {
                            return (
                                <span
                                    key={index}
                                    className={styles.pages_ellipsis}>
                                    {page}
                                </span>
                            );
                        } else {
                            return (
                                <AppButton
                                    color={
                                        page === candidateCurrentPage
                                            ? 'tealBlue'
                                            : 'gray'
                                    }
                                    isDisabled={isBatchProcessLoading}
                                    key={index}
                                    onClick={() => {
                                        setSelectedParticipants([]);
                                        setSearchParams({ page });
                                    }}>
                                    {' '}
                                    {page}
                                </AppButton>
                            );
                        }
                    })}
            </div>
            <div className={styles.footer_with_dialog_buttons}>
                <div className={styles.button_wrapper}>
                    <AppButton
                        icon={EnvelopeIcon}
                        color={
                            isSendReminderOrResetDisabled
                                ? 'grayDisabled'
                                : 'gray'
                        }
                        onClick={onSendResetPasswordEvent}
                        isDisabled={isSendReminderOrResetDisabled}>
                        Reset password
                    </AppButton>
                    <AppButton
                        icon={EnvelopeIcon}
                        color={
                            isSendReminderOrResetDisabled
                                ? 'grayDisabled'
                                : 'gray'
                        }
                        onClick={onSendReminderEvent}
                        isDisabled={isSendReminderOrResetDisabled}>
                        Send reminder
                    </AppButton>
                    <AppButton
                        icon={DocumentOpenIcon}
                        color={isSendReportDisabled ? 'grayDisabled' : 'gray'}
                        onClick={onSendReportEvent}
                        isDisabled={isSendReportDisabled}>
                        Send report
                    </AppButton>
                </div>
                <div className={styles.button_wrapper}>
                    <AppButton
                        icon={TrashIcon}
                        color={isDeleteDisabled ? 'grayDisabled' : 'red'}
                        onClick={onRemoveCandidateEvent}
                        isDisabled={isDeleteDisabled}
                        tooltipText={
                            usersCannotBeDeleted
                                ? 'The candidate(s) selected cannot be deleted because they have already completed the scan. Please contact support to delete candidate scan data.'
                                : undefined
                        }>
                        Remove
                    </AppButton>
                    {isSuperAdmin && (
                        <AppButton
                            icon={TrashIcon}
                            color={
                                selectedParticipants.length === 0
                                    ? 'grayDisabled'
                                    : 'red'
                            }
                            onClick={onFullDeleteCandidateEvent}
                            isDisabled={selectedParticipants.length === 0}>
                            Full delete
                        </AppButton>
                    )}
                    <AppButton
                        icon={ResetIcon}
                        color={
                            selectedParticipants.length !== 1
                                ? 'grayDisabled'
                                : 'red'
                        }
                        onClick={onResetCandidateScanEvent}
                        isDisabled={
                            selectedParticipants.length !== 1 ||
                            !selectedParticipants.every((p) =>
                                isSearchedParticipant(p)
                                    ? !!p.report_generated_at
                                    : !!(p as Participant).analysis,
                            )
                        }>
                        Reset scan
                    </AppButton>
                </div>
            </div>
        </div>
    );
};

export default TargetGroupTableFooter;
